import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { API_ENDPOINTS } from '../config';
import {
  EMBED_BENCHMARK_LABELS as BENCHMARK_LABELS,
  EMBED_DEFAULT_STRATEGY_COLORS as DEFAULT_STRATEGY_COLORS,
  EMBED_RANGE_KEYS as RANGE_KEYS,
  parseEmbedConfig,
} from '../utils/embedConfig';
import {
  rebaseSeriesToPeriodStart,
} from '../utils/vectorCapitalScenario';
import { windowPortfolioBlendSeries, windowStrategyPoints } from '../utils/strategyWindow';
import './EmbedChart.css';

const BENCHMARK_PILL_LABELS = {
  SPY: 'S&P 500', QQQ: 'NASDAQ', IEF: 'US 10Y', GLD: 'Gold', ES: 'E-mini S&P', NQ: 'E-mini NQ',
};
const BENCHMARK_COLORS = {
  SPY: '#ef4444', QQQ: '#f59e0b', IEF: '#22c55e', GLD: '#eab308', ES: '#06b6d4', NQ: '#a855f7',
};

function rangeStartDate(range) {
  if (range === 'All') return null;
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  if (range === 'YTD') return `${d.getUTCFullYear()}-01-01`;
  const months = { '1M': 1, '3M': 3, '6M': 6, '1Y': 12 }[range];
  if (!months) return null;
  d.setUTCMonth(d.getUTCMonth() - months);
  return d.toISOString().slice(0, 10);
}

function formatDateLabel(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit', timeZone: 'UTC' });
}

function formatMoney(v) {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `$${Math.round(n / 1000)}k`;
  return `$${Math.round(n)}`;
}

function EmbedTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="embed-chart-tooltip">
      <div className="embed-chart-tooltip-date">{label}</div>
      {payload.map((p) => (
        <div key={String(p.dataKey)} className="embed-chart-tooltip-line">
          <span className="dot" style={{ background: p.color }} />
          <span className="name">{p.name}</span>
          <span className="val">
            {Number(p.value).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function EmbedChart() {
  const config = useMemo(
    () => parseEmbedConfig(typeof window !== 'undefined' ? window.location.search : undefined),
    [],
  );
  const [strategySeries, setStrategySeries] = useState([]); // [{name, points}]
  const [benchmarkSeries, setBenchmarkSeries] = useState({}); // ticker -> [{date, normalized}]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // In-widget controls (params set the initial state). Strategies are
  // multi-select like Fyntrak's tabs — all offered strategies start active.
  const [activeStrategies, setActiveStrategies] = useState(() => new Set());
  const [activeBenchmarks, setActiveBenchmarks] = useState(
    () => new Set(config.benchmarkOptions.slice(0, 1)),
  );
  const [range, setRange] = useState(config.range);
  const [theme, setTheme] = useState(config.theme);

  // Load every offered strategy once; tabs switch client-side.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (!config.strategies.length) {
          setLoading(false);
          return;
        }
        const res = await fetch(API_ENDPOINTS.publicEmbedStrategies({
          names: config.strategies.join(','),
          entity: config.entity || undefined,
        }));
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        const list = Array.isArray(data?.strategies) ? data.strategies : [];
        setStrategySeries(list);
        setActiveStrategies((prev) => (prev.size ? prev : new Set(list.map((s) => s.name))));
        setLoading(false);
      } catch (_err) {
        if (cancelled) return;
        setError('Chart data is unavailable right now.');
        setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [config]);

  // Fetch each toggled-on benchmark's FULL history once; ranges window client-side.
  useEffect(() => {
    let cancelled = false;
    const missing = Array.from(activeBenchmarks).filter((t) => !benchmarkSeries[t]);
    if (!missing.length) return undefined;
    Promise.all(missing.map((ticker) =>
      fetch(API_ENDPOINTS.publicEmbedBenchmarkSeries({ benchmark: ticker, baseValue: config.capital }))
        .then((r) => r.json())
        .then((data) => [ticker, Array.isArray(data?.series) ? data.series : []])
        .catch(() => [ticker, []])))
      .then((entries) => {
        if (cancelled) return;
        setBenchmarkSeries((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
      });
    return () => { cancelled = true; };
  }, [activeBenchmarks, benchmarkSeries, config.capital]);

  const chartData = useMemo(() => {
    const startDate = rangeStartDate(range);
    const lines = [];
    let strategyColorIdx = 0;

    strategySeries.forEach((strat) => {
      if (!activeStrategies.has(strat.name)) return;
      const stratWindow = strat.portfolioBuild
        ? windowPortfolioBlendSeries(strat.portfolioBuild, startDate, config.capital)
        : windowStrategyPoints(strat.points || [], startDate, config.capital, strat.model);
      if (stratWindow.length < 2) return;
      lines.push({
        key: `s_${strategyColorIdx}`,
        name: strat.name,
        color: config.colors[strategyColorIdx] || DEFAULT_STRATEGY_COLORS[strategyColorIdx % DEFAULT_STRATEGY_COLORS.length],
        strategy: true,
        points: stratWindow,
      });
      strategyColorIdx += 1;
    });

    const strategyLines = lines.filter((line) => line.strategy);
    const stratStart = strategyLines
      .flatMap((line) => line.points.map((p) => p.date))
      .sort()[0] || startDate;
    const stratEnd = strategyLines
      .flatMap((line) => line.points.map((p) => p.date))
      .sort()
      .pop() || null;

    Array.from(activeBenchmarks).sort().forEach((ticker) => {
      const series = benchmarkSeries[ticker];
      if (!series?.length || !stratStart) return;
      const trimmed = series
        .map((p) => ({ date: String(p.date || '').slice(0, 10), normalized: Number(p.normalized) }))
        .filter((p) => p.date && Number.isFinite(p.normalized)
          && p.date >= stratStart
          && (!stratEnd || p.date <= stratEnd));
      if (trimmed.length < 2) return;
      const rebased = rebaseSeriesToPeriodStart(trimmed, config.capital);
      lines.push({
        key: `b_${ticker}`,
        name: BENCHMARK_LABELS[ticker] || ticker,
        color: BENCHMARK_COLORS[ticker] || '#94a3b8',
        strategy: false,
        points: rebased.map((p) => ({ date: p.date, value: p.normalized })),
      });
    });

    if (!lines.length) return { rows: [], lines: [] };

    // Union strategy + benchmark trading days; forward-fill both (Overview parity).
    const rangeStart = stratStart;

    const allDates = new Set();
    lines.forEach((line) => {
      line.points.forEach((p) => {
        if (!rangeStart || p.date >= rangeStart) allDates.add(p.date);
      });
    });

    const maps = lines.map((l) => ({
      key: l.key,
      map: new Map(l.points.map((p) => [p.date, p.value])),
      last: config.capital,
    }));
    const rows = Array.from(allDates).sort((a, b) => a.localeCompare(b)).map((date) => {
      const row = { date, label: formatDateLabel(date) };
      maps.forEach((m) => {
        const v = m.map.get(date);
        if (typeof v === 'number') m.last = v;
        row[m.key] = m.last;
      });
      return row;
    });
    return { rows, lines };
  }, [strategySeries, activeStrategies, benchmarkSeries, activeBenchmarks, range, config]);

  // Evenly spaced x-axis ticks (Fyntrak style): first date, last date, and
  // equal steps between — gridlines land on these instead of Recharts'
  // width-based auto ticks, which cluster unevenly.
  const xTicks = useMemo(() => {
    const rows = chartData.rows;
    if (rows.length <= 8) return rows.map((r) => r.label);
    const count = 7;
    const step = (rows.length - 1) / (count - 1);
    const out = [];
    for (let i = 0; i < count; i += 1) {
      out.push(rows[Math.round(i * step)].label);
    }
    return Array.from(new Set(out));
  }, [chartData.rows]);

  const toggleBenchmark = (ticker) => {
    setActiveBenchmarks((prev) => {
      const next = new Set(prev);
      if (next.has(ticker)) next.delete(ticker);
      else next.add(ticker);
      return next;
    });
  };

  // Multi-select like the dashboard: click to add/remove; the last active
  // strategy can't be removed (the chart always shows at least one).
  const toggleStrategy = (name) => {
    setActiveStrategies((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        if (next.size <= 1) return prev;
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const isLight = theme === 'light';
  const axisColor = isLight ? 'rgba(15, 23, 42, 0.62)' : 'rgba(255, 255, 255, 0.64)';
  const gridColor = isLight ? 'rgba(15, 23, 42, 0.16)' : 'rgba(255, 255, 255, 0.12)';
  const background = (isLight && config.theme === 'light' && config.bgOverride)
    ? config.bgOverride
    : (isLight ? '#f8fafc' : '#0b1120');

  const strategyTabs = strategySeries.map((s) => s.name);

  return (
    <div
      className={`embed-chart-page ${isLight ? 'embed-light' : 'embed-dark'}`}
      style={{ background }}
    >
      {(config.badge || config.title) && (
        <div className="embed-header">
          {config.badge ? (
            <span className="embed-live-badge">
              <span className="embed-live-dot" />
              {config.badge}
            </span>
          ) : <span />}
          {config.title ? <span className="embed-header-title">{config.title}</span> : null}
        </div>
      )}
      {config.controls && (
        <div className="embed-controls">
          <div className="embed-controls-row">
            <div className="embed-pills">
              {strategyTabs.map((name) => (
                <button
                  key={name}
                  type="button"
                  className={`embed-pill embed-pill-strategy ${activeStrategies.has(name) ? 'active' : ''}`}
                  onClick={() => toggleStrategy(name)}
                >
                  {name}
                </button>
              ))}
            </div>
            <div className="embed-pills embed-ranges">
              {RANGE_KEYS.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`embed-pill embed-pill-range ${key === range ? 'active' : ''}`}
                  onClick={() => setRange(key)}
                >
                  {key}
                </button>
              ))}
            </div>
          </div>
          <div className="embed-controls-row">
            <span className="embed-compare-label">Compare vs</span>
            <div className="embed-pills">
              {config.benchmarkOptions.map((ticker) => (
                <button
                  key={ticker}
                  type="button"
                  className={`embed-pill embed-pill-benchmark ${activeBenchmarks.has(ticker) ? 'active' : ''}`}
                  onClick={() => toggleBenchmark(ticker)}
                >
                  {BENCHMARK_PILL_LABELS[ticker] || ticker}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="embed-pill embed-theme-toggle"
              onClick={() => setTheme((t) => (t === 'light' ? 'dark' : 'light'))}
              aria-label="Toggle light/dark theme"
              title="Toggle light/dark theme"
            >
              {isLight ? '☾' : '☀'}
            </button>
          </div>
        </div>
      )}
      <div className="embed-chart-area">
        {loading ? (
          <div className="embed-chart-message">Loading…</div>
        ) : error ? (
          <div className="embed-chart-message">{error}</div>
        ) : chartData.rows.length === 0 ? (
          <div className="embed-chart-message">No public strategy data for this configuration.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData.rows} margin={{ top: 12, right: 24, left: 12, bottom: 8 }}>
              <CartesianGrid stroke={gridColor} strokeDasharray="4 4" />
              <XAxis dataKey="label" ticks={xTicks} interval={0} tick={{ fontSize: 11, fill: axisColor }} />
              <YAxis tick={{ fontSize: 11, fill: axisColor }} tickFormatter={formatMoney} domain={['auto', 'auto']} width={64} />
              <Tooltip content={<EmbedTooltip />} />
              <Legend verticalAlign="top" align="center" wrapperStyle={{ fontSize: 13, paddingBottom: 10 }} />
              {chartData.lines.map((line) => (
                <Line
                  key={line.key}
                  type="monotone"
                  dataKey={line.key}
                  name={line.name}
                  stroke={line.color}
                  strokeWidth={line.strategy ? 3 : 2}
                  dot={false}
                  isAnimationActive={false}
                  activeDot={{ r: 4 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
      {config.disclaimer ? (
        <div className="embed-disclaimer">{config.disclaimer}</div>
      ) : null}
    </div>
  );
}
